import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {adgroupLevelLabel} from '../../../utils/channelLevelLabels';
import {UI_VERSION_V2, useUiVersion} from '../../../context/UserModulesContext';
import {platformName} from '../../agency/connections_v2/platformsText';
import {Ga4AttributionModal} from '../../ga4_attribution/Ga4AttributionModal';
import {
    AGENCY_CARD_URL, isAutomatic, isKnownChannel, isNoneRule, lineAttributionFromAccountRule, ruleFromPayload,
} from '../../ga4_attribution/attributionModel';
import {RAW, capitalizeFirst, matchInlineText, ruleSummaryText} from '../../ga4_attribution/attributionText';
import {METHOD_INCLUDE_ADGROUP_ON_CONDITION, METHOD_SELECT_ADGROUPS_FROM_LIST} from './selectionMethods';
import '../../../sass/elements/_ga4-attribution.scss';

/**
 * Cómo se cruza GA4 con los grupos de anuncios y los anuncios de esta cuenta, en la línea, de SOLO
 * LECTURA (tanda «Cruce paid media ↔ GA4»: en la línea no se configura nada). La regla es de la
 * agencia o de la cuenta; acá se dice cuál vale, de dónde sale y qué alcanza en esta línea si está
 * armada por grupos. «Cambiar» abre la regla de la cuenta en un modal; sin la cuenta a mano, lleva a la
 * tarjeta de la agencia.
 *
 * `attribution` es el `ga4_attribution` que la línea trae por cuenta (`contrato.md` § 4). Sin él
 * (backend viejo) no se dibuja nada. `accountName` titula el modal.
 */
export function Ga4AttributionSummary({attribution, channel, selectionMethod, selectedAdgroups, accountName, onSaved}) {
    const {t} = useTranslation();
    const {setUiVersion} = useUiVersion();
    const [current, setCurrent] = useState(attribution);
    const [modalOpen, setModalOpen] = useState(false);

    // Lo que llega por props manda (otra cuenta, o la línea se volvió a pedir); el estado propio sólo
    // guarda lo que se acaba de guardar en el modal, hasta que el padre lo confirme.
    useEffect(() => {
        setCurrent(attribution);
    }, [attribution]);

    if (!current) return null;
    const lineChannel = current.channel || channel;
    if (!isKnownChannel(lineChannel)) return null;

    const rule = ruleFromPayload(current);
    const automatic = current.source === 'auto_google' || isAutomatic(lineChannel, current);
    const none = !automatic && (current.source === 'none' || isNoneRule(rule));
    const level = adgroupLevelLabel(t, lineChannel);
    const levelPlural = adgroupLevelLabel(t, lineChannel, {count: 2});

    let summary;
    if (automatic || none || !rule.adgroup_param || !rule.ad_param) {
        summary = ruleSummaryText(t, lineChannel, automatic ? {mode: 'AUTO_GOOGLE_ADS_LINK'} : rule);
    } else {
        summary = capitalizeFirst(t('ga4_attribution.line.summary', {
            levelPlural, adgroupParam: rule.adgroup_param, adParam: rule.ad_param,
            match: matchInlineText(t, rule.match_by), interpolation: RAW,
        }));
    }

    // De dónde sale la regla y, en una línea armada por grupos, qué tráfico cuenta: «Regla de la
    // agencia para Meta Ads. Esta línea cuenta solo el tráfico de sus 4 conjuntos de anuncios.»
    let sourceText = null;
    if (current.source === 'agency') {
        sourceText = t('ga4_attribution.source.agency', {platform: platformName(lineChannel), interpolation: RAW});
    } else if (current.source === 'account') {
        sourceText = t('ga4_attribution.source.account');
    }
    let scopeText = null;
    if (!automatic && !none && selectionMethod === METHOD_SELECT_ADGROUPS_FROM_LIST) {
        const count = Array.isArray(selectedAdgroups) ? selectedAdgroups.length : 0;
        scopeText = t('ga4_attribution.line.scope_selected', {count, level, levelPlural, interpolation: RAW});
    } else if (!automatic && !none && selectionMethod === METHOD_INCLUDE_ADGROUP_ON_CONDITION) {
        scopeText = t('ga4_attribution.line.scope_condition', {levelPlural, interpolation: RAW});
    }

    const actionLabel = t(none ? 'ga4_attribution.line.configure' : 'ga4_attribution.line.change');
    let action = null;
    if (!automatic && current.client_ad_account_id) {
        action = (
            <button type="button" className="ga4-attr-summary__action" onClick={() => setModalOpen(true)}>
                {actionLabel}
            </button>
        );
    } else if (!automatic) {
        // La tarjeta existe sólo en la 2.1: desde el clásico el link cambia de marco, como la nota del clásico.
        action = (
            <Link className="ga4-attr-summary__action" to={AGENCY_CARD_URL} onClick={() => setUiVersion(UI_VERSION_V2)}>
                {actionLabel}
            </Link>
        );
    }

    const handleSaved = (payload) => {
        const next = lineAttributionFromAccountRule(payload, current);
        setCurrent(next);
        if (onSaved) onSaved(next);
    };

    return (
        <div className="ga4-attr-summary" data-testid="ga4-attr-summary">
            <div className="ga4-attr-summary__main">
                <span className="ga4-attr-summary__text">{summary}</span>
                {action}
            </div>
            {(sourceText || scopeText) && (
                <p className="ga4-attr-summary__details">
                    {sourceText}{sourceText && scopeText ? '. ' : ''}{scopeText}
                </p>
            )}
            {current.client_ad_account_id && (
                <Ga4AttributionModal isOpen={modalOpen} onClose={() => setModalOpen(false)}
                                     clientAdAccountId={current.client_ad_account_id} channel={lineChannel}
                                     accountName={accountName} onSaved={handleSaved}/>
            )}
        </div>
    );
}

export default Ga4AttributionSummary;
